import {
  Badge,
  Box,
  HStack,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VisuallyHidden,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { TriangleAlert } from 'lucide-react';
import { Fragment, memo } from 'react';

import type { RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { UnitRequirementKind } from '@duke-hq/svc-api-db';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import {
  UNIT_REQUIREMENT_KIND_COLOURS,
  unitRequirementLabel,
  UnitRequirementKindHeading,
} from '../../../components/requirements/kinds';
import { incompleteSlideLabel } from '../../../UnitAssessment/incompleteSlideLabel';
import { GroupHeading } from './ReviewLayout';
import { testIds } from './testIds';

type Coverage = RouterOutputs['unitAssessment']['getDraftCoverage'];
type CoverageQuestion = Coverage['questions'][number];
type CoverageRequirement = Coverage['requirements'][number];

const QUESTION_COLUMN_WIDTH = pxToRem(64);
const BADGE_CHARACTER_WIDTH = 0.45;
const REQUIREMENT_CELL_WIDTH = 3.75;

function questionProblem({ blocker }: CoverageQuestion): string | null {
  switch (blocker) {
    case 'NO_LINKS':
      return t`No requirement linked.`;
    case 'FOUNDATION_SKILLS_ONLY':
      return t`Linked only to foundation skills, which do not place a question in a section.`;
    case 'FORM_NO_FIELDS':
    case 'QUESTION_GROUP_NO_QUESTIONS':
      return incompleteSlideLabel(blocker);
    case null:
      return null;
  }
}

function requirementProblem({ blocker }: CoverageRequirement): string | null {
  switch (blocker) {
    case 'NO_SECTION':
      return t`Not allocated to a content section, so a linked question cannot be placed.`;
    case 'NO_QUESTION':
      return t`No question linked.`;
    case null:
      return null;
  }
}

function badgeLabel(requirement: CoverageRequirement): string {
  return requirement.elementCode == null ? unitRequirementLabel(requirement) : requirement.code;
}

function requirementColumnWidth(requirements: CoverageRequirement[]): string {
  const longestLabel = Math.max(0, ...requirements.map((req) => badgeLabel(req).length));
  return `${longestLabel * BADGE_CHARACTER_WIDTH + REQUIREMENT_CELL_WIDTH}rem`;
}

function groupByKind(requirements: CoverageRequirement[]) {
  return Object.values(UnitRequirementKind).flatMap((kind) => {
    const rows = requirements.filter((requirement) => requirement.kind === kind);
    return rows.length === 0 ? [] : [{ kind, rows }];
  });
}

function groupByElement(rows: CoverageRequirement[]) {
  const elementCodes = [...new Set(rows.map(({ elementCode }) => elementCode))];

  return elementCodes.map((elementCode) => ({
    elementCode,
    rows: rows.filter((requirement) => requirement.elementCode === elementCode),
  }));
}

const TABLE_STYLES = {
  tableLayout: 'fixed',
  td: { py: 1 },
  'th, td': { transition: 'background-color 0.15s ease' },
  'tbody tr.requirement-row:not(.blocked) td:nth-child(even), tbody tr.requirement-row:not(.blocked):hover td':
    {
      bg: 'bg.subtle',
    },
  'tbody tr.blocked td': { bg: 'bg.danger-subtle' },
};

function LabelledCell({
  summary,
  problem,
  justify,
  children,
}: {
  summary: string;
  problem: string | null;
  justify: 'start' | 'center';
  children: React.ReactElement;
}) {
  return (
    <HStack spacing={1} justify={justify} minW={0}>
      <Tooltip label={summary} placement="top" hasArrow openDelay={150} shouldWrapChildren>
        {children}
      </Tooltip>
      {problem == null ? null : (
        <Tooltip label={problem} placement="top" hasArrow openDelay={150} shouldWrapChildren>
          <Icon
            as={TriangleAlert}
            boxSize={iconSize.sm}
            color="danger"
            flexShrink={0}
            role="img"
            aria-label={problem}
            data-testid={testIds.coverageProblem}
          />
        </Tooltip>
      )}
    </HStack>
  );
}

function QuestionHeader({ question }: { question: CoverageQuestion }) {
  const problem = questionProblem(question);

  return (
    <Th w={QUESTION_COLUMN_WIDTH} textAlign="center" data-testid={testIds.coverageQuestionHeader}>
      <LabelledCell summary={question.title} problem={problem} justify="center">
        <Badge
          colorScheme={problem == null ? 'gray' : 'red'}
          variant={problem == null ? 'subtle' : 'outline'}
          rounded="full"
          px={2}
          fontSize="xs"
        >
          {question.label}
        </Badge>
      </LabelledCell>
    </Th>
  );
}

function KindHeadingRow({
  kind,
  coveredCount,
  requirementCount,
  span,
}: {
  kind: UnitRequirementKind;
  coveredCount: number;
  requirementCount: number;
  span: number;
}) {
  return (
    <Tr bg="bg.subtle">
      <Td position="sticky" left={0} zIndex={1} bg="bg.subtle">
        <HStack spacing={3} whiteSpace="nowrap">
          <GroupHeading>
            <UnitRequirementKindHeading kind={kind} />
          </GroupHeading>
          <Text
            fontSize="xs"
            color={coveredCount === requirementCount ? 'fg.muted' : 'danger.deep'}
          >
            <Trans>
              {coveredCount} of {requirementCount} covered
            </Trans>
          </Text>
        </HStack>
      </Td>
      <Td colSpan={span} />
    </Tr>
  );
}

function ElementHeadingRow({ code, span }: { code: string; span: number }) {
  return (
    <Tr>
      <Td position="sticky" left={0} zIndex={1} bg="bg.surface">
        <Text fontSize="xs" fontWeight="semibold" color="fg.muted" whiteSpace="nowrap">
          <Trans>Element {code}</Trans>
        </Text>
      </Td>
      <Td colSpan={span} />
    </Tr>
  );
}

function CoverageCell({ isLinked, colour }: { isLinked: boolean; colour: string }) {
  return (
    <Td textAlign="center">
      {isLinked ? (
        <Box
          display="inline-block"
          boxSize={2.5}
          borderRadius="full"
          bg={`${colour}.500`}
          aria-hidden
          data-testid={testIds.coverageDot}
        />
      ) : null}
      <VisuallyHidden>{isLinked ? t`Linked` : t`Not linked`}</VisuallyHidden>
    </Td>
  );
}

function RequirementRow({
  requirement,
  questions,
}: {
  requirement: CoverageRequirement;
  questions: CoverageQuestion[];
}) {
  const colour = UNIT_REQUIREMENT_KIND_COLOURS[requirement.kind];
  const label = unitRequirementLabel(requirement);
  const problem = requirementProblem(requirement);

  return (
    <Tr className={problem == null ? 'requirement-row' : 'requirement-row blocked'}>
      <Td position="sticky" left={0} zIndex={1} bg="bg.surface">
        <LabelledCell
          summary={`${label}: ${requirement.description}`}
          problem={problem}
          justify="start"
        >
          <Badge
            colorScheme={colour}
            variant={problem == null ? 'subtle' : 'outline'}
            fontFamily="mono"
            fontSize="xs"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {badgeLabel(requirement)}
          </Badge>
        </LabelledCell>
      </Td>

      {questions.map((question) => (
        <CoverageCell
          key={question.slideId}
          isLinked={question.requirementIds.includes(requirement.id)}
          colour={colour}
        />
      ))}
    </Tr>
  );
}

export const CoverageReviewTable = memo(function CoverageReviewTable({
  requirements,
  questions,
}: {
  requirements: CoverageRequirement[];
  questions: CoverageQuestion[];
}) {
  const requirementColumn = requirementColumnWidth(requirements);

  return (
    <TableContainer overflowX="auto" overflowY="auto" flex={1} minH={0}>
      <Table
        size="sm"
        variant="simple"
        w={`calc(${requirementColumn} + ${questions.length} * ${QUESTION_COLUMN_WIDTH})`}
        sx={TABLE_STYLES}
      >
        <Thead position="sticky" top={0} zIndex={2} bg="bg.surface">
          <Tr>
            <Th position="sticky" left={0} zIndex={3} w={requirementColumn} bg="bg.surface">
              <VisuallyHidden>
                <Trans>Requirement</Trans>
              </VisuallyHidden>
            </Th>
            {questions.map((question) => (
              <QuestionHeader key={question.slideId} question={question} />
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {groupByKind(requirements).map(({ kind, rows }) => (
            <Fragment key={kind}>
              <KindHeadingRow
                kind={kind}
                coveredCount={rows.filter(({ blocker }) => blocker == null).length}
                requirementCount={rows.length}
                span={questions.length}
              />

              {groupByElement(rows).map(({ elementCode, rows: elementRows }) => (
                <Fragment key={elementCode ?? 'no-element'}>
                  {elementCode == null ? null : (
                    <ElementHeadingRow code={elementCode} span={questions.length} />
                  )}

                  {elementRows.map((requirement) => (
                    <RequirementRow
                      key={requirement.id}
                      requirement={requirement}
                      questions={questions}
                    />
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
});
